// src/components/dashboard/Orders.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Eye, Trash, TrashWhite, DeliveredColors } from "../../assets/icons/icons";
import axios from "axios";
import CustomAlert from "../CustomAlert";

/* ---- Helpers ---- */
const parseNum = (v, d = 0) => {
  const n = Number.parseFloat(String(v ?? "").toString().replace(",", "."));
  return Number.isFinite(n) ? n : d;
};
const money = (n) => `${parseNum(n).toFixed(2)} JOD`;
const normalize = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();

const pickName = (it) => {
  if (it?.name) return it.name;
  if (it?.item_data?.product_name_E) return it.item_data.product_name_E;
  if (it?.product?.product_name_E) return it.product.product_name_E;
  if (it?.accessory?.accessories_name_E) return it.accessory.accessories_name_E;
  return "Unknown";
};
const pickColor = (it) =>
  it?.item_data?.selected_color_name ??
  it?.item_data?.selected_color ??
  it?.variant_color ??
  "";
const pickCapacity = (it) =>
  it?.item_data?.selected_capacity ??
  it?.variant_capacity ??
  "";

/* --- Models helpers (يدعم model_name بأنواعه: نص/CSV/JSON/Array) --- */
const toArray = (val) => {
  if (!val) return [];
  // Array: رجّع أسماء جاهزة
  if (Array.isArray(val)) {
    return val
      .map((x) =>
        typeof x === "string"
          ? x.trim()
          : (x?.model_name || x?.name || x?.label || String(x || "")).trim()
      )
      .filter(Boolean);
  }
  // String: جرّب JSON أولاً، ثم CSV / فواصل شائعة
  if (typeof val === "string") {
    const s = val.trim();
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return toArray(parsed);
    } catch {}
    return s.split(/[,|/]+/).map((t) => t.trim()).filter(Boolean);
  }
  // Object: جرّب مفاتيح مألوفة
  if (typeof val === "object") {
    const s = val?.model_name || val?.name || val?.label;
    return s ? [String(s).trim()] : [];
  }
  return [];
};

const pickModels = (it) => {
  const d = it?.item_data || {};
  // نقرأ من model_name (جذر/داخل item_data) + حقول بديلة
  const candidates = [
    it?.model_name,
    d?.model_name,

    it?.selected_models,
    d?.selected_models,

    it?.accessories_selected_models,
    d?.accessories_selected_models,

    it?.models,
    d?.models,

    it?.selected_model,
    d?.selected_model,
  ];
  for (const c of candidates) {
    const arr = toArray(c);
    if (arr.length) return arr;
  }
  return [];
};

/** رقم الدليفري */
const pickDeliveryNum = (o) => parseNum(o?.delivery ?? o?.shipping, 0);

/**
 * ناخذ سعر الوحدة من لقطة الـ checkout (الأولوية العليا)،
 * ثم نحاول السعة من الـ item_data، ثم السعر الأساسي كحلّ أخير.
 */
const getItemUnitFromSnapshot = (it) => {
  if (it?.unit_price != null && parseNum(it.unit_price) > 0) return parseNum(it.unit_price);
  if (it?.price != null && parseNum(it.price) > 0) return parseNum(it.price);

  const d = it?.item_data || {};
  if (d?.unit_price != null && parseNum(d.unit_price) > 0) return parseNum(d.unit_price);
  if (d?.price != null && parseNum(d.price) > 0) return parseNum(d.price);
  if (d?.product_price != null && parseNum(d.product_price) > 0) return parseNum(d.product_price);

  const cap = normalize(it?.variant_capacity ?? d?.selected_capacity ?? it?.capacity);
  const mapsRaw = [d?.product_capacity_prices, it?.product_capacity_prices, it?.product?.product_capacity_prices].filter(Boolean);

  for (let m of mapsRaw) {
    if (typeof m === "string") {
      try { m = JSON.parse(m); } catch { m = null; }
    }
    if (m && typeof m === "object") {
      for (const [k, v] of Object.entries(m)) {
        if (normalize(k) === cap) return parseNum(v);
      }
    }
  }

  if (d?.product_price_original != null && parseNum(d.product_price_original) > 0) return parseNum(d.product_price_original);
  if (it?.product?.product_price != null) return parseNum(it.product.product_price);
  if (it?.accessory?.accessories_price != null) return parseNum(it.accessory.accessories_price);
  return 0;
};

/** إجمالي الطلب = مجموع العناصر + الدليفري */
const calcOrderTotalFromItems = (order) => {
  const itemsTotal = Array.isArray(order?.items)
    ? order.items.reduce((s, it) => s + getItemUnitFromSnapshot(it) * (parseNum(it.quantity, 1)), 0)
    : 0;
  const delivery = pickDeliveryNum(order);
  const total = itemsTotal + delivery;
  return total < 0 ? 0 : total;
};

const showTotal = (order) => {
  const fromApi = parseNum(order?.total, NaN);
  const finalNum = Number.isFinite(fromApi) && fromApi >= 0 ? fromApi : calcOrderTotalFromItems(order);
  return money(finalNum + 1);
};

/** كوبون للعرض */
const couponDisplay = (o) => {
  const candidates = [
    o?.coupon_code, o?.couponCode, o?.coupon?.code, o?.coupon?.coupon_code,
    o?.coupon?.name, o?.order_meta?.coupon_code, o?.orderMeta?.coupon_code,
    o?.meta?.coupon_code, typeof o?.coupon === "string" ? o.coupon : null,
  ].filter(Boolean);
  if (candidates.length) return candidates[0];
  const first = Array.isArray(o?.items) ? o.items[0] : null;
  const firstCoupon =
    first?.item_data?.coupon_code ||
    first?.item_data?.coupon ||
    first?.item_data?.meta?.coupon_code ||
    null;
  if (firstCoupon) return firstCoupon;
  if (o?.coupon_id) return `#${o.coupon_id}`;
  return "—";
};

// سبنر
const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4A4 4 0 008 12H4z" />
  </svg>
);

/* Coercion: نحول حقول الأسعار/الكميات لأرقام */
const coerceItemNumbers = (it) => ({
  ...it,
  quantity: parseNum(it?.quantity, 1),
  price: parseNum(it?.price, 0),
  unit_price: parseNum(it?.unit_price, 0),
  item_data: {
    ...it?.item_data,
    price: parseNum(it?.item_data?.price, 0),
    unit_price: parseNum(it?.item_data?.unit_price, 0),
    product_price: parseNum(it?.item_data?.product_price, 0),
    product_price_original: parseNum(it?.item_data?.product_price_original, 0),
    product_price_discount: parseNum(it?.item_data?.product_price_discount, 0),
  },
});

const coerceOrderNumbers = (o) => {
  const items = Array.isArray(o?.items) ? o.items.map(coerceItemNumbers) : [];
  const subtotal = parseNum(o?.subtotal, NaN);
  const delivery = pickDeliveryNum(o);
  const apiTotal = parseNum(o?.total, NaN);

  const itemsSubtotal = items.reduce((s, it) => s + getItemUnitFromSnapshot(it) * (parseNum(it.quantity, 1)), 0);
  const safeSubtotal = Number.isFinite(subtotal) ? subtotal : itemsSubtotal;

  const computedTotal = itemsSubtotal + delivery;
  const safeTotal = Number.isFinite(apiTotal) && apiTotal >= 0 ? apiTotal : computedTotal;

  return {
    ...o,
    items,
    subtotal: parseNum(safeSubtotal, 0),
    delivery,
    shipping: delivery,
    tax: parseNum(o?.tax, 0),
    total: parseNum(safeTotal, 0),
  };
};

/* ===== axios anti-cache defaults (حماية إضافية) ===== */
try {
  axios.defaults.headers.get = {
    ...(axios.defaults.headers.get || {}),
    "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  };
} catch {}

export default function Orders() {
  const API_BASE = "https://laravel-api.aj-mart.com/api";

  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertMsg, setAlertMsg] = useState({ type: "", message: "" });

  const [deletingIds, setDeletingIds] = useState([]);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [orderToUpdate, setOrderToUpdate] = useState(null);
  const [updateData, setUpdateData] = useState({ order_status: "Pending", payment_status: "Pending" });

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }, []);

  useEffect(() => {
    fetchOrders(true);
  }, []);

  /* جلب الطلبات مع bust للكاش */
  const fetchOrders = async (bust = false) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/orders`, {
        params: { _ts: bust ? Date.now() : undefined },
        headers: {
          "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        withCredentials: true,
      });
      if (res.data?.status) {
        const payload = res.data.data?.orders;
        const ordersData = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
        setOrders(ordersData.map(coerceOrderNumbers));
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setAlertMsg({ type: "error", message: "Failed to fetch orders" });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const openUpdateDialogWith = (order, field, value) => {
    setOrderToUpdate(order);
    setUpdateData({
      order_status: field === "order_status" ? value : order.order_status || "Pending",
      payment_status: field === "payment_status" ? value : order.payment_status || "Pending",
    });
    setShowUpdateDialog(true);
  };

  const confirmUpdate = async () => {
    setShowUpdateDialog(false);
    if (!orderToUpdate) return;
    try {
      const res = await axios.put(`${API_BASE}/orders/update/${orderToUpdate.id}`, updateData);
      if (res.data?.status) {
        setAlertMsg({ type: "success", message: res.data.message || "Order updated successfully" });
        // إعادة جلب fresh بعد التحديث
        await fetchOrders(true);
      } else {
        setAlertMsg({ type: "error", message: res.data?.message || "Failed to update order" });
      }
    } catch (err) {
      console.error("Error updating order:", err);
      setAlertMsg({ type: "error", message: err.response?.data?.message || "An unexpected error occurred" });
    } finally {
      setOrderToUpdate(null);
    }
  };

  const handleDeleteClick = (orderId) => {
    setOrderToDelete(orderId);
    setShowDeleteDialog(true);
  };

  // حذف تفاؤلي + إعادة جلب مع bust
  const confirmDelete = async () => {
    const id = orderToDelete;
    setShowDeleteDialog(false);
    if (!id) return;

    setDeletingIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    const prevOrders = orders;
    setOrders((cur) => cur.filter((o) => o.id !== id));
    setSelectedOrders((cur) => cur.filter((x) => x !== id));

    try {
      const res = await axios.delete(`${API_BASE}/orders/delete/${id}`);
      if (res.data?.status) {
        setAlertMsg({ type: "success", message: res.data.message || "Order deleted successfully" });
        // مهم: جلب جديد مع cache-buster عشان الـ CDN
        await fetchOrders(true);
      } else {
        setAlertMsg({ type: "error", message: res.data?.message || "Failed to delete order" });
        setOrders(prevOrders);
      }
    } catch (err) {
      console.error("Error deleting order:", err);
      setAlertMsg({ type: "error", message: err.response?.data?.message || "An unexpected error occurred" });
      setOrders(prevOrders);
    } finally {
      setDeletingIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const handleBulkDeleteClick = () => {
    if (!selectedOrders.length) {
      setAlertMsg({ type: "error", message: "Please select orders to delete" });
      return;
    }
    setShowBulkDeleteDialog(true);
  };

  // حذف جماعي + إعادة جلب مع bust
  const confirmBulkDelete = async () => {
    const ids = [...selectedOrders];
    setShowBulkDeleteDialog(false);
    if (!ids.length) return;

    setDeletingIds((prev) => [...new Set([...prev, ...ids])]);
    const prevOrders = orders;
    setOrders((cur) => cur.filter((o) => !ids.includes(o.id)));
    setSelectedOrders([]);

    try {
      const results = await Promise.allSettled(ids.map((id) => axios.delete(`${API_BASE}/orders/delete/${id}`)));
      const failed = results.filter((r) => r.status === "rejected" || (r.value && r.value.data && r.value.data.status === false));
      if (failed.length) {
        setAlertMsg({ type: "error", message: "Some deletions failed. Refreshing…" });
        await fetchOrders(true);
      } else {
        setAlertMsg({ type: "success", message: "Selected orders deleted successfully" });
        await fetchOrders(true);
      }
    } catch (err) {
      console.error("Error deleting orders:", err);
      setAlertMsg({ type: "error", message: err.response?.data?.message || "Failed to delete selected orders" });
      setOrders(prevOrders);
    } finally {
      setDeletingIds((prev) => prev.filter((x) => !ids.includes(x)));
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders((prev) => (prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]));
  };
  const handleSelectAll = (e) => {
    setSelectedOrders(e.target.checked ? orders.map((o) => o.id) : []);
  };

  const itemsToText = (order) => {
    if (!order?.items?.length) return "No Items";
    const rows = order.items.map((it) => {
      const name = pickName(it);
      const qty = parseNum(it.quantity, 1);
      const color = pickColor(it);
      const cap = pickCapacity(it);
      const models = pickModels(it);

      const extras = [];
      if (color) extras.push(color);
      if (cap) extras.push(cap);
      if (models.length) extras.push(`${models.length > 1 ? "Models" : "Model"}: ${models.join(", ")}`);

      return `${name}${extras.length ? ` (${extras.join(" / ")})` : ""} x${qty}`;
    });
    return rows.join(", ");
  };

  const badge = (val, palette) => {
    const color = palette?.[val] || "bg-gray-100 text-gray-700";
    return <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${color}`}>{val || "—"}</span>;
  };

  const orderStatusColor = {
    Pending: "bg-yellow-100 text-yellow-700",
    Processing: "bg-blue-100 text-blue-700",
    Shipped: "bg-indigo-100 text-indigo-700",
    Delivered: "bg-green-100 text-green-700",
    Cancelled: "bg-red-100 text-red-700",
    Completed: "bg-emerald-100 text-emerald-700",
  };

  const paymentStatusColor = {
    Pending: "bg-yellow-100 text-yellow-700",
    Unpaid: "bg-yellow-100 text-yellow-700",
    Paid: "bg-green-100 text-green-700",
    Failed: "bg-red-100 text-red-700",
  };

  if (loading) return <div className="py-[25px]">Loading orders...</div>;

  return (
    <section className="py-[25px]">
      <div className="main-title mb-[20px]">Orders</div>
      {alertMsg.message && (
        <CustomAlert type={alertMsg.type} message={alertMsg.message} onClose={() => setAlertMsg({ type: "", message: "" })} />
      )}
      <div className="flex justify-end items-center gap-[10px] mb-[20px]">
        <button
          className={`btn bg-red-500 hover:bg-red-600 flex items-center gap-2 ${!selectedOrders.length ? "opacity-50 cursor-not-allowed" : ""}`}
          onClick={handleBulkDeleteClick}
          disabled={!selectedOrders.length}
        >
          <TrashWhite />
          <span>Delete Orders ({selectedOrders.length})</span>
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-200 text-left">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-200 p-[10px]">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={orders.length > 0 && selectedOrders.length === orders.length}
                />
              </th>
              <th className="border border-gray-200 p-[10px]">No.</th>
              <th className="border border-gray-200 p-[10px]">Customer</th>
              <th className="border border-gray-200 p-[10px]">Order List</th>
              <th className="border border-gray-200 p-[10px]">Order Status</th>
              <th className="border border-gray-200 p-[10px]">Payment Status</th>
              <th className="border border-gray-200 p-[10px]">Order Price</th>
              <th className="border border-gray-200 p-[10px]">Payment Type</th>
              <th className="border border-gray-200 p-[10px]">Coupon</th>
              <th className="border border-gray-200 p-[10px]">Created</th>
              <th className="border border-gray-200 p-[10px]">Control Tools</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, index) => {
              const isRowDeleting = deletingIds.includes(order.id);
              return (
                <tr key={order.id} className={`bg-white hover:bg-gray-50 ${isRowDeleting ? "opacity-60" : ""}`}>
                  <td className="border border-gray-200 p-[10px]">
                    <input
                      type="checkbox"
                      checked={selectedOrders.includes(order.id)}
                      onChange={() => handleSelectOrder(order.id)}
                      disabled={isRowDeleting}
                    />
                  </td>
                  <td className="border border-gray-200 p-[10px]">{index + 1}</td>
                  <td className="border border-gray-200 p-[10px]">{order.full_name || "—"}</td>
                  <td className="border border-gray-200 p-[10px]">{itemsToText(order)}</td>
                  <td className="border border-gray-200 p-[10px]">
                    <div className="flex items-center gap-2">
                      {badge(order.order_status || "Pending", orderStatusColor)}
                      <select
                        value={order.order_status || "Pending"}
                        onChange={(e) => openUpdateDialogWith(order, "order_status", e.target.value)}
                        className="px-[10px] py-[6px] border rounded bg-[#f6f6f6] text-sm"
                        disabled={isRowDeleting}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Processing">Processing</option>
                        <option value="Shipped">Shipped</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                  </td>
                  <td className="border border-gray-200 p-[10px]">
                    <div className="flex items-center gap-2">
                      {badge(order.payment_status || "Pending", paymentStatusColor)}
                      <select
                        value={order.payment_status || "Pending"}
                        onChange={(e) => openUpdateDialogWith(order, "payment_status", e.target.value)}
                        className="px-[10px] py-[6px] border rounded bg-[#f6f6f6] text-sm"
                        disabled={isRowDeleting}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Paid">Paid</option>
                        <option value="Failed">Unpaid</option>
                      </select>
                    </div>
                  </td>

                  <td className="border border-gray-200 p-[10px]">{showTotal(order)}</td>

                  <td className="border border-gray-200 p-[10px]">{order.payment_type || "—"}</td>
                  <td className="border border-gray-200 p-[10px]">{couponDisplay(order)}</td>
                  <td className="border border-gray-200 p-[10px]">
                    {order.created_at ? new Date(order.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="border border-gray-200 p-[10px]">
                    <div className="flex items-center gap-3">
                      <Link to={`/orderDetails/${order.id}`} className={`cursor-pointer ${isRowDeleting ? "pointer-events-none opacity-50" : ""}`} title="View">
                        <Eye />
                      </Link>
                      <button
                        className="cursor-pointer inline-flex items-center gap-1"
                        onClick={() => handleDeleteClick(order.id)}
                        title="Delete"
                        disabled={isRowDeleting}
                      >
                        {isRowDeleting ? <Spinner /> : <Trash />}
                      </button>
                      <button
                        className={`cursor-pointer ${isRowDeleting ? "pointer-events-none opacity-50" : ""}`}
                        onClick={() => {
                          setOrderToUpdate(order);
                          setUpdateData({
                            order_status: order.order_status || "Pending",
                            payment_status: order.payment_status || "Pending",
                          });
                          setShowUpdateDialog(true);
                        }}
                        title="Quick Update"
                        disabled={isRowDeleting}
                      >
                        <DeliveredColors />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!orders.length && (
              <tr>
                <td className="border p-[10px]" colSpan={11}>
                  No orders found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Update dialog */}
      {showUpdateDialog && orderToUpdate && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white w-[420px] rounded-lg shadow-lg p-5">
            <h2 className="text-xl font-bold mb-4">Update Order</h2>
            <div className="flex flex-col gap-4">
              <label className="capitalize">
                Order Status:
                <select
                  name="order_status"
                  value={updateData.order_status}
                  onChange={(e) => setUpdateData((p) => ({ ...p, order_status: e.target.value }))}
                  className="px-[10px] py-[5px] border rounded ml-2"
                >
                  <option value="Pending">Pending</option>
                  <option value="Processing">Processing</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </label>

              <label className="capitalize">
                Payment Status:
                <select
                  name="payment_status"
                  value={updateData.payment_status}
                  onChange={(e) => setUpdateData((p) => ({ ...p, payment_status: e.target.value }))}
                  className="px-[10px] py-[5px] border rounded ml-2"
                >
                  <option value="Pending">Pending</option>
                  <option value="Paid">Paid</option>
                  <option value="Failed">Unpaid</option>
                </select>
              </label>
            </div>

            <div className="flex justify-end gap-4 mt-4">
              <button className="btn bg-gray-300 hover:bg-gray-400" onClick={() => setShowUpdateDialog(false)}>
                Cancel
              </button>
              <button className="btn bg-green-500 hover:bg-green-600" onClick={confirmUpdate}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete dialogs */}
      {showDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white w-[400px] rounded-lg shadow-lg p-5 text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this order?</p>
            <div className="flex justify-center gap-4">
              <button className="btn bg-gray-300 hover:bg-gray-400" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </button>
              <button className="btn bg-red-500 hover:bg-red-600" onClick={confirmDelete}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white w-[400px] rounded-lg shadow-lg p-5 text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Bulk Deletion</h2>
            <p className="mb-4">Are you sure you want to delete {selectedOrders.length} orders?</p>
            <div className="flex justify-center gap-4">
              <button className="btn bg-gray-300 hover:bg-gray-400" onClick={() => setShowBulkDeleteDialog(false)}>
                Cancel
              </button>
              <button className="btn bg-red-500 hover:bg-red-600" onClick={confirmBulkDelete}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
